import { useEffect, useRef, useState } from "react";
import Player from "@vimeo/player";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

interface VimeoPlayerProps {
  videoId: string;
  title?: string;
  initialProgress?: {
    seconds_watched: number;
    percent_watched: number;
    last_position_sec: number;
    furthest_watched_sec?: number;
    completed_at: string | null;
  };
  onProgressUpdate?: (progressData: {
    seconds_watched: number;
    percent_watched: number;
    last_position_sec: number;
    furthest_watched_sec: number;
    duration: number;
  }) => void;
  onImmediateProgressUpdate?: (percent: number) => void;
  onLessonComplete?: () => void;
}

export function VimeoPlayer({
  videoId,
  title,
  initialProgress,
  onProgressUpdate,
  onImmediateProgressUpdate,
  onLessonComplete,
}: VimeoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  

  // Use refs to store callback functions and avoid recreating player
  const onProgressUpdateRef = useRef(onProgressUpdate);
  const onImmediateProgressUpdateRef = useRef(onImmediateProgressUpdate);
  const onLessonCompleteRef = useRef(onLessonComplete);
  const lastUpdateRef = useRef<number>(0);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const durationRef = useRef<number>(0);
  const furthestWatchedRef = useRef<number>(
    initialProgress?.furthest_watched_sec || initialProgress?.last_position_sec || 0
  );
  const isInitializingRef = useRef<boolean>(true);
  const isSeekingRef = useRef<boolean>(false);
  const lastNaturalPositionRef = useRef<number>(0);

  // Update refs when props change
  useEffect(() => {
    onProgressUpdateRef.current = onProgressUpdate;
  }, [onProgressUpdate]);

  useEffect(() => {
    onImmediateProgressUpdateRef.current = onImmediateProgressUpdate;
  }, [onImmediateProgressUpdate]);

  useEffect(() => {
    onLessonCompleteRef.current = onLessonComplete;
  }, [onLessonComplete]);


  // Only recreate player when videoId changes
  useEffect(() => {
    if (!containerRef.current || !videoId) return;

    // Create the player
    const player = new Player(containerRef.current, {
      id: parseInt(videoId),
      width: 800,
      responsive: true,
      title: false,
    });

    playerRef.current = player;

    // Set up event listeners
    player.ready().then(async () => {
      setIsLoading(false);

      // Get video duration
      try {
        const videoDuration = await player.getDuration();
        durationRef.current = videoDuration;

        // Initialize furthest watched percentage
        const initialFurthest = initialProgress?.furthest_watched_sec || initialProgress?.last_position_sec || 0;
        const initialFurthestPercent = parseInt(((initialFurthest / videoDuration) * 100).toString());
        
        // Immediately send initial progress to sidebar
        onImmediateProgressUpdateRef.current?.(initialFurthestPercent);
        
        // Resume from last position if there's progress
        if (initialProgress && initialProgress.last_position_sec > 10) {
          await player.setCurrentTime(initialProgress.last_position_sec);
        }
      } catch (error) {
        console.error("Error setting up player:", error);
      }

      // Set up event listeners after player is ready
      player.on("timeupdate", (data) => {
        if (durationRef.current > 0) {
          // Mark initialization as complete after first timeupdate
          isInitializingRef.current = false;
          
          // Only update furthest position if user is naturally watching (not seeking)
          if (!isSeekingRef.current) {
            // Natural progression - only allow reasonable forward movement
            const maxNaturalJump = 3; // Max 3 seconds per timeupdate event
            const timeSinceLastNatural = Math.abs(data.seconds - lastNaturalPositionRef.current);
            
            if (timeSinceLastNatural <= maxNaturalJump && data.seconds > furthestWatchedRef.current) {
              furthestWatchedRef.current = data.seconds;
              lastNaturalPositionRef.current = data.seconds;
            } else if (data.seconds <= furthestWatchedRef.current) {
              // User is reviewing previous content - this is fine
              lastNaturalPositionRef.current = data.seconds;
            }
          } else {
            // Currently seeking - don't update furthest position, just track where we are
            lastNaturalPositionRef.current = data.seconds;
            // Clear seeking flag after a short delay
            setTimeout(() => {
              isSeekingRef.current = false;
            }, 500);
          }
          
          const currentFurthest = furthestWatchedRef.current;
          
          // Calculate furthest watched percentage
          const furthestPercent = parseInt(((currentFurthest / durationRef.current) * 100).toString());
          
          // Send update to sidebar when percentage changes
          onImmediateProgressUpdateRef.current?.(furthestPercent);

          const now = Date.now();

          // Throttle database saves to every 5 seconds
          if (now - lastUpdateRef.current > 5000) {
            lastUpdateRef.current = now;

            const progressData = {
              seconds_watched: data.seconds,
              percent_watched: data.percent,
              last_position_sec: data.seconds,
              furthest_watched_sec: furthestWatchedRef.current,
              duration: durationRef.current,
            };

            // Debounce the save operation
            if (updateIntervalRef.current) {
              clearTimeout(updateIntervalRef.current);
            }

            updateIntervalRef.current = setTimeout(() => {
              onProgressUpdateRef.current?.(progressData);
            }, 1000);
          }
        }
      });

      // Prevent seeking beyond furthest watched position
      player.on("seeking", async (data) => {
        // Mark that we're currently seeking
        isSeekingRef.current = true;
        
        // Skip enforcement during initialization (resume position seeking)
        if (isInitializingRef.current) {
          return;
        }
        
        const currentFurthest = furthestWatchedRef.current;
        const gracePeriod = 10; // Reduced grace period
        
        if (data.seconds > currentFurthest + gracePeriod) {
          // User tried to jump too far ahead - snap them back
          await player.setCurrentTime(currentFurthest);
        }
      });

      player.on("ended", () => {
        onImmediateProgressUpdateRef.current?.(100);
        onLessonCompleteRef.current?.();
      });
    });

    // Cleanup
    return () => {
      if (updateIntervalRef.current) {
        clearTimeout(updateIntervalRef.current);
      }
      player.destroy();
    };
  }, [videoId]);

  return (
    <div className="space-y-4">
      {/* Progress indicator - moved above video */}
      {/* {!isLoading && furthestWatchedPercent > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isCompleted && <CheckCircle2 className="h-4 w-4 text-green-500" />}
              <span className="text-sm font-medium text-foreground">
                {isCompleted ? "Lesson Completed" : `${Math.round(furthestWatchedPercent)}% watched`}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">
              {Math.round(furthestWatchedPercent)}%
            </span>
          </div>
          <Progress
            value={Math.min(furthestWatchedPercent, 100)}
            className="h-2"
          />
        </div>
      )} */}

      <Card className="overflow-hidden">
        <div className="relative aspect-video bg-black">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          )}
          <div ref={containerRef} className="w-full h-full" />
        </div>
        {title && (
          <div className="p-4 border-t">
            <h3 className="font-semibold text-lg text-foreground">{title}</h3>
          </div>
        )}
      </Card>
    </div>
  );
}
